import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import UsersDrawer from '../../../userManagement/components/UsersDrawer';
import { Confirm } from '../../../userManagement/components/Confirm';
import { useSnackbar } from 'notistack';
import { StyledDataGrid } from '../../UserRequestsStyles';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useTheme } from '@material-ui/core/styles';
import AddWhitelistDomainModal from './AddWhitelistDomainModal';
import { buildWhitelistColumns } from './columns';
import { useUserDateFormatter } from '../../hooks';
import {
  WhitelistDrawerProps,
  WhitelistForm,
  WhitelistDomain,
  WhitelistDomainPageRequest,
} from './types';
import {
  useCreateWhitelistDomain,
  useDeleteWhitelistDomain,
  useUpdateWhitelistDomainActive,
  useWhitelistDomains,
} from './hooks/useWhitelistDomains';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';

export const WhitelistDrawer = ({ isOpen, onClose, readOnly = false }: WhitelistDrawerProps) => {
  const [openModal, setOpenModal] = useState(false);
  const { formatDateTime } = useUserDateFormatter();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);

  const pageRequest = useMemo<WhitelistDomainPageRequest>(
    () => ({
      page: paginationModel.page,
      limit: paginationModel.pageSize,
    }),
    [paginationModel.page, paginationModel.pageSize],
  );

  const { enqueueSnackbar } = useSnackbar();
  const { palette } = useTheme();
  const whitelistQuery = useWhitelistDomains(pageRequest, { enabled: isOpen });
  const {
    isLoading,
    isFetching,
    isError,
    error,
    data: whitelistPage,
  } = whitelistQuery;
  const { mutateAsync: createWhitelistDomain, isPending: isCreating } =
    useCreateWhitelistDomain();
  const { mutateAsync: deleteWhitelistDomain, isPending: isDeleting } =
    useDeleteWhitelistDomain();
  const { mutateAsync: updateWhitelistDomainStatus } =
    useUpdateWhitelistDomainActive();

  const whitelistItems = useMemo<WhitelistDomain[]>(() => {
    if (!whitelistPage) {
      return [];
    }
    return whitelistPage.elements;
  }, [whitelistPage]);

  useEffect(() => {
    if (isError && error) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to load whitelist domains'),
        {
          variant: 'error',
        },
      );
    }
  }, [enqueueSnackbar, error, isError]);

  const rows = useMemo(() => {
    if (whitelistItems.length === 0) {
      return [
        {
          id: 'placeholder',
          domain: '--',
          createdAt: '--',
          note: '--',
          active: false,
        },
      ];
    }

    return whitelistItems.map(item => ({
      id: item.id,
      domain: item.domain,
      createdAt: item.createdDate ? formatDateTime(item.createdDate) : '--',
      note: item.note ?? '',
      active: item.active,
    }));
  }, [whitelistItems, formatDateTime]);

  const handleAddDomain = async (values: WhitelistForm) => {
    if (isCreating) {
      return;
    }
    try {
      await createWhitelistDomain({
        domain: values.domain,
        note: values.note?.trim() ? values.note.trim() : undefined,
      });
      setOpenModal(false);
      enqueueSnackbar('Domain added to whitelist', { variant: 'success' });
    } catch (mutationError) {
      const message = getApiErrorMessage(
        mutationError,
        'Failed to add domain to whitelist',
      );
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const deleteDomain = useCallback((id: string) => setDeleteId(id), []);

  const handleToggleDomainStatus = useCallback(
    async (id: string, nextActive: boolean) => {
      if (statusUpdatingId === id) {
        return;
      }

      setStatusUpdatingId(id);
      try {
        await updateWhitelistDomainStatus({ id, isActive: nextActive });
        enqueueSnackbar(
          `Whitelist domain is ${nextActive ? 'activated' : 'deactivated'}`,
          { variant: 'success' },
        );
      } catch (mutationError) {
        const message = getApiErrorMessage(
          mutationError,
          'Failed to update domain status',
        );
        enqueueSnackbar(message, { variant: 'error' });
      } finally {
        setStatusUpdatingId(current => (current === id ? null : current));
      }
    },
    [enqueueSnackbar, statusUpdatingId, updateWhitelistDomainStatus],
  );

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    if (isDeleting) {
      return;
    }
    try {
      await deleteWhitelistDomain(deleteId);
      setDeleteId(null);
      enqueueSnackbar('Domain removed from whitelist', {
        variant: 'success',
      });
    } catch (mutationError) {
      const message = getApiErrorMessage(
        mutationError,
        'Failed to remove domain from whitelist',
      );
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const columns: GridColDef[] = useMemo(
    () =>
      buildWhitelistColumns({
        deleteDomain,
        palette,
        toggleDomainStatus: handleToggleDomainStatus,
        statusUpdatingId,
        readOnly,
      }),
    [deleteDomain, handleToggleDomainStatus, palette, readOnly, statusUpdatingId],
  );

  const rowCount = whitelistPage?.total ?? 0;

  return (
    <UsersDrawer
      isOpen={isOpen}
      handleDrawerClose={onClose}
      id="whitelist-drawer"
    >
      <Box mb={6}>
        <Typography variant="h3">Manage Whitelisted Domain</Typography>
      </Box>
      {!readOnly && (
        <Box mb={4} display="flex" justifyContent="flex-start">
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            color="primary"
            onClick={() => setOpenModal(true)}
          >
            Add Whitelisted Domain
          </Button>
        </Box>
      )}
      <StyledDataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={model => setPaginationModel(model)}
        rowCount={rowCount}
        autoHeight
        hideFooter={whitelistItems.length === 0}
        loading={isLoading || isFetching}
      />
      <AddWhitelistDomainModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        existingDomains={whitelistItems.map(item => item.domain)}
        onSubmit={handleAddDomain}
      />

      <Confirm
        isOpen={Boolean(deleteId)}
        title="Are you sure you want to remove this domain?"
        content="Registration requests from this domain will no longer be automatically approved."
        confirm="Remove Domain"
        cancel="Cancel"
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteId(null)}
      />
    </UsersDrawer>
  );
};

export default WhitelistDrawer;
